/**
 * Progress indicator component showing evaluation progress
 */

import React from 'react'
import { ProgressBar } from '@fs/zion-ui'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'

export interface ProgressIndicatorProps {
  total: number
  currentPosition: number
}

const containerCss = css`
  min-width: 220px;
`

const getMessageStatus = (percentage: number): 'normal' | 'complete' => {
  return percentage === 100 ? 'complete' : 'normal'
}

const getMotivationMessage = (t: (key: string, defaultValue: string) => string, percentage: number): string => {
  if (percentage === 0)
    return t('transliteration-eval-page.progress-indicator.lets-start.message', "Let's get started!")
  if (percentage < 10) return t('transliteration-eval-page.progress-indicator.nice-start.message', 'Nice start!')
  if (percentage < 25)
    return t('transliteration-eval-page.progress-indicator.making-progress.message', 'Making progress...')
  if (percentage === 25) return t('transliteration-eval-page.progress-indicator.quarter-done.message', 'Quarter done!')
  if (percentage < 50) return t('transliteration-eval-page.progress-indicator.keep-going.message', 'Keep going!')
  if (percentage === 50)
    return t('transliteration-eval-page.progress-indicator.halfway-there.message', 'Halfway there!')
  if (percentage < 75) return t('transliteration-eval-page.progress-indicator.over-halfway.message', 'Over halfway!')
  if (percentage === 75)
    return t('transliteration-eval-page.progress-indicator.three-quarters.message', 'Three quarters!')
  if (percentage < 90) return t('transliteration-eval-page.progress-indicator.almost-there.message', 'Almost there!')
  if (percentage < 100) return t('transliteration-eval-page.progress-indicator.final-stretch.message', 'Final stretch!')
  return t('transliteration-eval-page.progress-indicator.complete.message', 'Complete!')
}

export const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({ total, currentPosition }) => {
  const [t] = useTranslation()
  const percentage = total > 0 ? Math.round((currentPosition / total) * 100) : 0
  const motivationMessage = getMotivationMessage(t, percentage)
  const messageStatus = getMessageStatus(percentage)

  return (
    <div css={containerCss}>
      <ProgressBar
        key={`progress-${currentPosition}-${total}`}
        value={currentPosition}
        maxValue={total}
        color="green"
        message={motivationMessage}
        messageStatus={messageStatus}
      >
        {`${currentPosition} / ${total}`}
      </ProgressBar>
    </div>
  )
}
